/**
 * Pantalla de acceso con dos puertas:
 *  - Correo @launitec.com + código de 6 dígitos que llega por email (el equipo).
 *  - PIN del espacio, cómodo para el iPad.
 * Tras varios fallos el servidor exige CAPTCHA y luego bloquea por un rato.
 */
import { api, ApiError, UnauthorizedError, type LoginError, type LoginState, type SessionInfo } from '../api.ts'
import { h } from './dom.ts'
import { icon } from './icons.ts'

export interface LoginOptions {
  onSuccess: (session: SessionInfo) => void
}

const FALLBACK_STATE: LoginState = {
  codeLength: 4,
  numeric: true,
  captchaRequired: false,
  locked: false,
  retryAfterMs: 0,
  attemptsLeft: 3,
  emailEnabled: false,
  emailDomain: 'launitec.com',
}

export async function renderLogin(root: HTMLElement, options: LoginOptions): Promise<void> {
  let state: LoginState
  try {
    state = { ...FALLBACK_STATE, ...(await api.loginState()) }
  } catch {
    state = FALLBACK_STATE
  }

  let mode: 'email' | 'pin' = state.emailEnabled ? 'email' : 'pin'
  let captchaId: string | null = null
  let sentTo: string | null = null
  let busy = false

  const error = h('p', { class: 'login-error', style: 'display:none' }, [''])
  const hint = h('p', { class: 'login-hint', style: 'display:none' }, [''])
  const body = h('div', {})
  const modeSwitch = h('div', { class: 'role-pills', style: 'margin:0 0 16px' })

  /* ── piezas compartidas ─────────────────────────────────── */

  const captchaBox = h('div', { class: 'captcha', style: 'display:none' })
  const captchaImage = h('div', { class: 'captcha-image' })
  const captchaInput = h('input', {
    class: 'input',
    placeholder: 'Escribe el código de la imagen',
    autocapitalize: 'characters',
    autocomplete: 'off',
    spellcheck: false,
  }) as HTMLInputElement
  captchaBox.append(
    h('label', {}, ['Verificación']),
    h('div', { class: 'captcha-row' }, [
      captchaImage,
      h('button', { class: 'tbtn', title: 'Otro código', html: icon('redo', 18), onclick: () => void loadCaptcha() }),
    ]),
    captchaInput,
  )

  const setError = (message: string): void => {
    error.textContent = message
    error.style.display = message ? 'block' : 'none'
  }
  const setHint = (message: string): void => {
    hint.textContent = message
    hint.style.display = message ? 'block' : 'none'
  }

  async function loadCaptcha(): Promise<void> {
    try {
      const challenge = await api.captcha()
      captchaId = challenge.id
      captchaImage.innerHTML = challenge.svg
      captchaInput.value = ''
      captchaBox.style.display = 'flex'
    } catch {
      captchaBox.style.display = 'none'
    }
  }

  function lockUi(detail: LoginError | null, controls: HTMLElement[]): void {
    if (!detail?.locked || !detail.retryAfterMs) return
    const until = Date.now() + detail.retryAfterMs
    const tick = (): void => {
      const left = until - Date.now()
      if (left <= 0) {
        for (const c of controls) (c as HTMLInputElement).disabled = false
        setError('')
        return
      }
      for (const c of controls) (c as HTMLInputElement).disabled = true
      const seconds = Math.ceil(left / 1000)
      setError(`Demasiados intentos. Espera ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}.`)
      setTimeout(tick, 1000)
    }
    tick()
  }

  /** Casillas de dígitos con avance automático, borrado y pegado. */
  function digitBoxes(count: number, onComplete: () => void): { row: HTMLElement; boxes: HTMLInputElement[]; value: () => string; clear: () => void } {
    const boxes: HTMLInputElement[] = []
    const row = h('div', { class: 'pin-row' })
    for (let i = 0; i < count; i++) {
      const box = h('input', {
        class: 'pin-box',
        type: 'tel',
        inputmode: 'numeric',
        pattern: '[0-9]*',
        maxlength: 1,
        autocomplete: i === 0 ? 'one-time-code' : 'off',
        'aria-label': `Dígito ${i + 1}`,
      }) as HTMLInputElement
      boxes.push(box)
      row.append(box)
    }
    const value = (): string => boxes.map((b) => b.value).join('')
    const clear = (): void => {
      for (const b of boxes) b.value = ''
      boxes[0]?.focus()
    }
    boxes.forEach((box, index) => {
      box.addEventListener('input', () => {
        box.value = box.value.replace(/\D/g, '').slice(-1)
        if (box.value && index < count - 1) boxes[index + 1].focus()
        if (value().length === count) onComplete()
      })
      box.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent
        if (ev.key === 'Backspace' && !box.value && index > 0) {
          ev.preventDefault()
          boxes[index - 1].focus()
          boxes[index - 1].value = ''
        }
        if (ev.key === 'Enter') onComplete()
      })
      box.addEventListener('paste', (e) => {
        const digits = ((e as ClipboardEvent).clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, count)
        if (!digits) return
        e.preventDefault()
        digits.split('').forEach((d, i) => {
          if (boxes[i]) boxes[i].value = d
        })
        boxes[Math.min(digits.length, count - 1)].focus()
        if (digits.length === count) onComplete()
      })
      box.addEventListener('focus', () => box.select())
    })
    return { row, boxes, value, clear }
  }

  /* ── puerta 1: correo de la empresa ─────────────────────── */

  function renderEmailMode(): void {
    const emailInput = h('input', {
      class: 'input',
      type: 'email',
      placeholder: `galo  ·  o  galo@${state.emailDomain}`,
      autocomplete: 'email',
      autocapitalize: 'off',
      spellcheck: false,
      enterkeyhint: 'send',
      value: localStorage.getItem('wb.email') ?? '',
    }) as HTMLInputElement

    const sendBtn = h('button', { class: 'btn-primary', onclick: () => void sendCode() }, [
      'Enviarme el código',
    ]) as HTMLButtonElement

    const codeSection = h('div', { style: 'display:none' })
    const code = digitBoxes(6, () => void confirmCode())
    const resendBtn = h('button', { class: 'btn-secondary', onclick: () => void sendCode(true) }, [
      'Reenviar código',
    ]) as HTMLButtonElement
    const backBtn = h('button', { class: 'btn-secondary', onclick: () => renderEmailMode() }, ['Cambiar correo'])
    codeSection.append(
      h('div', { class: 'field' }, [h('label', {}, ['Código que llegó a tu correo']), code.row]),
      h('div', { style: 'display:flex;gap:8px;margin-top:4px' }, [resendBtn, backBtn]),
    )

    /** "galo" vale igual que "galo@launitec.com": el dominio se completa solo. */
    function fullEmail(): string {
      const value = emailInput.value.trim().toLowerCase()
      return value.includes('@') ? value : value ? `${value}@${state.emailDomain}` : ''
    }

    async function sendCode(again = false): Promise<void> {
      const email = fullEmail()
      if (!email.endsWith(`@${state.emailDomain}`)) {
        setError(`Tiene que ser un correo @${state.emailDomain} (o solo tu usuario)`)
        emailInput.focus()
        return
      }
      if (busy) return
      busy = true
      sendBtn.disabled = true
      resendBtn.disabled = true
      setError('')
      setHint(again ? 'Reenviando…' : 'Enviando el código…')
      try {
        // Con presión de intentos el servidor también pide el CAPTCHA para
        // mandar el correo; la caja es la misma que usa el PIN.
        await api.requestEmailCode(
          email,
          captchaId ?? undefined,
          captchaBox.style.display !== 'none' ? captchaInput.value : undefined,
        )
        localStorage.setItem('wb.email', email)
        sentTo = email
        setHint(`Te mandamos un código a ${email}. Llega en unos segundos; revisa también spam.`)
        emailInput.disabled = true
        sendBtn.style.display = 'none'
        codeSection.style.display = 'block'
        code.clear()
      } catch (e) {
        setHint('')
        setError(e instanceof Error ? e.message : 'No se pudo enviar el código')
        const detail = e instanceof UnauthorizedError || e instanceof ApiError ? e.detail : null
        if (detail?.captchaRequired) await loadCaptcha()
      } finally {
        busy = false
        sendBtn.disabled = false
        resendBtn.disabled = false
      }
    }

    async function confirmCode(): Promise<void> {
      if (busy || !sentTo) return
      const value = code.value()
      if (value.length !== 6) return
      busy = true
      setError('')
      try {
        const session = await api.login({
          email: sentTo,
          code: value,
          captchaId: captchaId ?? undefined,
          captchaAnswer: captchaBox.style.display !== 'none' ? captchaInput.value : undefined,
        })
        localStorage.setItem('wb.name', session.name)
        options.onSuccess(session)
      } catch (e) {
        const detail = e instanceof UnauthorizedError ? e.detail : null
        setError(e instanceof Error ? e.message : 'No se pudo entrar')
        code.clear()
        if (detail?.captchaRequired) await loadCaptcha()
        lockUi(detail, code.boxes)
      } finally {
        busy = false
      }
    }

    emailInput.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') void sendCode()
    })

    body.replaceChildren(
      h('div', { class: 'field' }, [
        h('label', {}, ['Correo de La Unitec']),
        emailInput,
        h('small', { class: 'field-hint' }, [`Puedes escribir solo tu usuario; le ponemos @${state.emailDomain}`]),
      ]),
      h('div', { class: 'dialog-actions', style: 'margin-top:8px' }, [sendBtn]),
      codeSection,
    )
    setHint('')
    setError('')
    sentTo = null
    emailInput.focus()
  }

  /* ── puerta 2: PIN del espacio ──────────────────────────── */

  function renderPinMode(): void {
    const nameInput = h('input', {
      class: 'input',
      placeholder: 'Tu nombre',
      value: localStorage.getItem('wb.name') ?? '',
      autocomplete: 'name',
      autocapitalize: 'words',
    }) as HTMLInputElement

    const pin = digitBoxes(state.codeLength, () => void submit())
    const submitBtn = h('button', { class: 'btn-primary', onclick: () => void submit() }, [
      'Entrar',
    ]) as HTMLButtonElement

    async function submit(): Promise<void> {
      if (busy) return
      const name = nameInput.value.trim()
      if (!name) {
        setError('Escribe tu nombre.')
        nameInput.focus()
        return
      }
      const value = pin.value()
      if (value.length !== state.codeLength) return
      busy = true
      submitBtn.disabled = true
      setError('')
      try {
        const session = await api.login({
          pin: value,
          name,
          captchaId: captchaId ?? undefined,
          captchaAnswer: captchaBox.style.display !== 'none' ? captchaInput.value : undefined,
        })
        localStorage.setItem('wb.name', name)
        options.onSuccess(session)
      } catch (e) {
        const detail = e instanceof UnauthorizedError ? e.detail : null
        setError(e instanceof Error ? e.message : 'No se pudo entrar')
        pin.clear()
        if (detail?.captchaRequired) await loadCaptcha()
        lockUi(detail, [...pin.boxes, submitBtn])
      } finally {
        busy = false
        submitBtn.disabled = false
      }
    }

    nameInput.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') pin.boxes[0]?.focus()
    })

    body.replaceChildren(
      h('div', { class: 'field' }, [h('label', {}, ['Nombre']), nameInput]),
      h('div', { class: 'field' }, [h('label', {}, [`PIN de ${state.codeLength} dígitos`]), pin.row]),
      h('div', { class: 'dialog-actions', style: 'margin-top:8px' }, [submitBtn]),
    )
    setHint('')
    setError('')
    if (nameInput.value) pin.boxes[0]?.focus()
    else nameInput.focus()
  }

  function setMode(next: 'email' | 'pin'): void {
    mode = next
    for (const pill of Array.from(modeSwitch.children)) {
      pill.classList.toggle('is-active', (pill as HTMLElement).dataset.mode === mode)
    }
    if (mode === 'email') renderEmailMode()
    else renderPinMode()
  }

  if (state.emailEnabled) {
    modeSwitch.append(
      h('button', { class: 'pill', 'data-mode': 'email', onclick: () => setMode('email') }, ['Correo de trabajo']),
      h('button', { class: 'pill', 'data-mode': 'pin', onclick: () => setMode('pin') }, ['PIN del espacio']),
    )
  }

  root.replaceChildren(
    h('div', { class: 'scrim', style: 'background:var(--bg-app)' }, [
      h('div', { class: 'dialog login-card' }, [
        h('div', { class: 'brand-mark', style: 'margin-bottom:18px' }, [
          h('div', { class: 'logo', html: icon('pen', 22) }),
          h('div', {}, [
            h('h1', { style: 'font-size:18px' }, ['Pizarra La Unitec']),
            h('span', {}, ['Lienzo colaborativo en tiempo real']),
          ]),
        ]),
        modeSwitch,
        body,
        captchaBox,
        hint,
        error,
      ]),
    ]),
  )

  setMode(mode)
  if (state.captchaRequired || state.locked) await loadCaptcha()
}
